import {
  LpBuyerQuoteBlock,
  LpFaqBlock,
  LpHonestFilterBlock,
  LpOutcomesBlock,
} from "@/puck/landingBlocks";
import type { LandingComponents } from "@/puck/blocks/blockTypes";

export const proofConfigs: Pick<
  LandingComponents,
  "LpOutcomes" | "LpBuyerQuote" | "LpHonestFilter" | "LpFaq"
> = {
  LpOutcomes: {
    label: "Outcomes (stats)",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      attributionSeparator: { type: "text" },
      background: {
        type: "select",
        options: [
          { label: "Transparent", value: "transparent" },
          { label: "Voice card", value: "voiceCard" },
        ],
      },
      items: {
        type: "array",
        getItemSummary: (item) => item.eyebrow,
        defaultItemProps: {
          eyebrow: "Segment",
          stat: "$100M",
          statSuffix: "",
          captionLead: "What this measures.",
          captionHighlight: "",
          captionTail: "",
          quote: "What the operator said.",
          attributionName: "Title",
          attributionRole: "company",
        },
        arrayFields: {
          eyebrow: { type: "text", contentEditable: true },
          stat: { type: "text", contentEditable: true },
          statSuffix: { type: "text" },
          captionLead: { type: "textarea", contentEditable: true },
          captionHighlight: { type: "text", contentEditable: true },
          captionTail: { type: "text", contentEditable: true },
          quote: { type: "textarea", contentEditable: true },
          attributionName: { type: "text", contentEditable: true },
          attributionRole: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "Customer outcomes",
      titleLead: "What teams running this",
      titleAccent: "actually recovered.",
      subtitle: "Anonymized, real, recent.",
      attributionSeparator: " · ",
      background: "transparent",
      items: [
        { eyebrow: "Network · Multi-creditor", stat: "$300M", statSuffix: "", captionLead: "Collected across the network · last 12 months.", captionHighlight: "", captionTail: "", quote: "We finally work the whole book every day.", attributionName: "Head of Recovery", attributionRole: "debt-buyer network" },
        { eyebrow: "Lender · Installment", stat: "$100M", statSuffix: "", captionLead: "Recovered this year · single deployment.", captionHighlight: "", captionTail: "", quote: "We didn't grow the floor.", attributionName: "VP Collections", attributionRole: "consumer lender" },
        { eyebrow: "Healthcare AR", stat: "10,400", statSuffix: "/day", captionLead: "Accounts worked. A call center in a box.", captionHighlight: "", captionTail: "", quote: "We ran flat and Gail handled the surge.", attributionName: "Director of Patient Accounts", attributionRole: "hospital network" },
      ],
    },
    render: LpOutcomesBlock,
  },
  LpBuyerQuote: {
    label: "Buyer quote (gradient)",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      quote: { type: "textarea", contentEditable: true },
      attributionName: { type: "text", contentEditable: true },
      attributionRole: { type: "text", contentEditable: true },
    },
    defaultProps: {
      eyebrow: "Proof from the floor",
      titleLead: "A real buyer in their",
      titleAccent: "own words.",
      subtitle: "No spin. No script. Just the operator who runs the number.",
      quote: "Gail worked every delinquent account. $1M recovered in 5 weeks.",
      attributionName: "Director of Collections",
      attributionRole: "Consumer Lender",
    },
    render: LpBuyerQuoteBlock,
  },
  LpHonestFilter: {
    label: "Honest filter (yes/no)",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      background: {
        type: "select",
        options: [
          { label: "Transparent", value: "transparent" },
          { label: "Voice card", value: "voiceCard" },
        ],
      },
      columns: {
        type: "array",
        getItemSummary: (item) => item.title,
        defaultItemProps: {
          title: "For you if",
          badge: "Yes",
          variant: "yes",
          items: [{ segments: [{ text: "A qualifying point.", bold: false }] }],
        },
        arrayFields: {
          title: { type: "text", contentEditable: true },
          badge: { type: "text" },
          variant: {
            type: "select",
            options: [
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ],
          },
          items: {
            type: "array",
            label: "Rows",
            getItemSummary: (_item, index) =>
              `Row ${typeof index === "number" ? index + 1 : ""}`,
            defaultItemProps: { segments: [{ text: "Row text.", bold: false }] },
            arrayFields: {
              segments: {
                type: "array",
                getItemSummary: (item) => item.text,
                defaultItemProps: { text: "Text", bold: false },
                arrayFields: {
                  text: { type: "text", contentEditable: true },
                  bold: {
                    type: "radio",
                    options: [
                      { label: "Normal", value: false },
                      { label: "Bold", value: true },
                    ],
                  },
                },
              },
            },
          },
        },
      },
    },
    defaultProps: {
      eyebrow: "Honest filter",
      titleLead: "This is for you if you run a",
      titleAccent: "collections floor.",
      background: "transparent",
      columns: [
        {
          title: "For you if",
          badge: "Yes",
          variant: "yes",
          items: [
            { segments: [{ text: "You run an " }, { text: "in-house team", bold: true }, { text: "." }] },
          ],
        },
        {
          title: "Not for you if",
          badge: "No",
          variant: "no",
          items: [
            { segments: [{ text: "You're shopping for a " }, { text: "generic chatbot", bold: true }, { text: "." }] },
          ],
        },
      ],
    },
    render: LpHonestFilterBlock,
  },
  LpFaq: {
    label: "FAQ (accordion)",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      background: {
        type: "select",
        options: [
          { label: "Voice card", value: "voiceCard" },
          { label: "Surface", value: "surface" },
        ],
      },
      items: {
        type: "array",
        getItemSummary: (item) => item.question,
        defaultItemProps: { question: "Question", answer: "Answer" },
        arrayFields: {
          question: { type: "text", contentEditable: true },
          answer: { type: "textarea", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "Frequently asked",
      titleLead: "Questions your",
      titleAccent: "team will ask.",
      background: "voiceCard",
      items: [
        { question: "How does compliance work?", answer: "Built around the relevant rules from day one." },
        { question: "What's the implementation timeline?", answer: "Days, not months." },
      ],
    },
    render: LpFaqBlock,
  },
};
